import type { Point } from '@ipfabric/utils';
import { produce } from 'immer';
import { useLayoutEffect, useState } from 'react';
import deepEqual from 'react-fast-compare';

interface Props {
  container: HTMLElement | null;
  position: Point;
}

interface ContextMenuPositionHook {
  position: Point;
  refCallback: (element: HTMLElement | null) => void;
}

export const useContextMenuPosition = (
  props: Props,
): ContextMenuPositionHook => {
  const [fixedPosition, setFixedPosition] = useState<Point | null>(null);

  const refCallback = (element: HTMLElement | null) => {
    if (
      // element may be just updating to fixedPosition (ref callback is called twice on updates,
      // first time with null), so don't reset anything here to prevent infinite loop
      element === null ||
      props.container === null ||
      props.position === null
    ) {
      return;
    }

    const updatedPosition = produce(props.position, (draft) => {
      const containerHeight = props.container!.offsetHeight;
      const elementHeight = element.offsetHeight;

      if (containerHeight < draft.y + elementHeight) {
        draft.y -= elementHeight;
      }

      const containerWidth = props.container!.offsetWidth;
      const elementWidth = element.offsetWidth;

      if (containerWidth < draft.x + elementWidth) {
        draft.x -= elementWidth;
      }
    });

    if (!deepEqual(fixedPosition, updatedPosition)) {
      setFixedPosition(updatedPosition);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies(props.container): should get the update
  useLayoutEffect(() => {
    setFixedPosition(props.position);
  }, [props.position, props.container]);

  return {
    position: fixedPosition ?? props.position,
    refCallback,
  };
};
